/**
 * REST nonce handling.
 *
 * The nonce used to arrive only inside the page HTML. Full-page caching then
 * served that HTML for far longer than a nonce is valid, so visitors given a
 * stale cached page failed every request with no way to recover — and could
 * not even report it, because the error beacon is nonce-checked too.
 *
 * The value printed into the page is now treated as a seed. When the server
 * rejects it we fetch a fresh one and replay, so a stale page self-heals on
 * the first question instead of erroring.
 */

// Live value for this page, seeded from the (possibly stale) printed one.
let currentNonce = null;

// Shared in-flight request, so a burst of callers triggers one fetch.
let inFlight = null;

function sessionUrl(restConfig) {
  if (restConfig && restConfig.sessionUrl) return restConfig.sessionUrl;

  // Derived rather than required. The pages that break are precisely the ones
  // served from cache, whose HTML predates this change and will never carry a
  // sessionUrl — so it has to be reachable from what an old page did print.
  const chatUrl = (restConfig && restConfig.url) || "";
  if (chatUrl) {
    return chatUrl.replace(/\/(chat|prompt)\/?$/, "/session");
  }
  return "/wp-json/cmw/v1/session";
}

/**
 * The nonce to send. Uses what we already hold, falling back to the page's
 * seed, and only goes to the network when neither exists.
 */
export async function getNonce(restConfig) {
  if (currentNonce) return currentNonce;
  if (restConfig && restConfig.nonce) {
    currentNonce = restConfig.nonce;
    return currentNonce;
  }
  return refreshNonce(restConfig);
}

/**
 * Fetch a new nonce and adopt it. Also written back onto restConfig so
 * sendHeartbeat — which reads restConfig.nonce directly — recovers with us,
 * closing the hole where a stale nonce silently suppressed error reporting.
 *
 * Returns "" if the fetch fails; the caller surfaces the original error.
 */
export function refreshNonce(restConfig) {
  if (inFlight) return inFlight;

  inFlight = fetch(sessionUrl(restConfig), {
    method: "GET",
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json" },
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      const nonce = data && typeof data.nonce === "string" ? data.nonce : "";
      if (nonce) {
        currentNonce = nonce;
        if (restConfig) restConfig.nonce = nonce;
      }
      return nonce;
    })
    .catch(() => "")
    .then((nonce) => {
      inFlight = null;
      return nonce;
    });

  return inFlight;
}

// Codes that mean "your nonce is no longer good", from either this plugin or
// WordPress core.
const NONCE_CODES = {
  cmw_invalid_nonce: true,
  rest_cookie_invalid_nonce: true,
};

export function isNonceFailure(err) {
  return !!(err && NONCE_CODES[err.code]);
}
